import store from '@/store'
import cognitoAuth from '@/cognito'
import { isGenuineAuthError } from '@/cognito/cognito'
import { EventBus } from '@/event-bus/event-bus.js'
import { authHelpers } from '@/api/authHelpers'
import errorReporter from '@/utils/errorReporter'
import { track, flushNow } from '@/utils/telemetry'

// Retry transient network failures on the GraphQL fetch. The save path
// (updateWorkflow / updateCards) was dying on the same ERR_NETWORK_CHANGED /
// "Failed to fetch" interface blips as the AI calls. Only the fetch itself is
// retried — a 401, HTTP error, or GraphQL error is thrown AFTER the response
// resolves and is NOT retried. A retried write that actually landed will hit the
// optimistic-lock conflict path (which already refetches the latest version), so
// there is no data loss — at worst a spurious "modified by another user" notice.
const GRAPHQL_NETWORK_RETRIES = 2
const RETRY_BASE_MS = 600
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

let cypressRunning
try {
  if (Cypress) {
    cypressRunning = true
  }
} catch (e) {
  cypressRunning = false
}

class GraphQLClient {
  constructor (endpoint) {
    if (cypressRunning) {
      this.endpoint = Cypress.env('VUE_APP_APPSYNC_GRAPHQL_ENDPOINT')
    } else {
      this.endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
    }
  }

  async query ({ operationName, query, variables = {} }, { failSilently = false } = {}) {
    try {
      // A 401 means the token was rejected. On the FIRST 401 we force a token refresh
      // and retry ONCE before treating it as a session failure: a token can expire
      // mid-flight during a long operation (e.g. a multi-minute AI generation), and
      // ejecting the user for a token the refresh token can still renew turns a blip
      // into a forced logout. Only a refresh that GENUINELY fails ends the session.
      let response
      let didAuthRefresh = false
      let refreshFailedTransiently = false
      while (true) {
        const jwtToken = await cognitoAuth.getIdTokenPromise()
        const fetchOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: jwtToken
          },
          body: JSON.stringify({
            operationName,
            query,
            variables
          })
        }

        // Only the network-level fetch is retried (fetch rejects solely on a network
        // failure or an abort). Everything after — 401, !ok, GraphQL errors — is
        // evaluated on the resolved response and handled below, not retried.
        let attempt = 0
        while (true) {
          try {
            response = await fetch(this.endpoint, fetchOptions)
            break
          } catch (fetchError) {
            if (fetchError.name === 'AbortError') {
              throw fetchError
            }
            attempt++
            if (attempt <= GRAPHQL_NETWORK_RETRIES) {
              track('graphql_retry', { operation: operationName, attempt, message: String(fetchError?.message || '').slice(0, 160) })
              await sleep(RETRY_BASE_MS * attempt)
              continue
            }
            throw fetchError
          }
        }

        if (response.status === 401 && !didAuthRefresh) {
          didAuthRefresh = true
          try {
            await cognitoAuth.refreshSession()
            track('graphql_auth_retry', { operation: operationName })
            continue // re-read the (now refreshed) token and retry the request once
          } catch (refreshError) {
            // A non-auth (e.g. network) refresh failure is transient — the session may
            // still be valid, so do NOT log out; surface a retryable error instead.
            refreshFailedTransiently = !isGenuineAuthError(refreshError)
          }
        }
        break
      }

      if (response.status === 401) {
        if (refreshFailedTransiently) {
          // Token refresh failed for a non-auth reason; keep the user signed in.
          throw new Error('Network error during token refresh')
        }
        // The refreshed token was still rejected (or refresh genuinely failed) — a
        // real session failure. Anonymous (public link / embed) viewers have no
        // session to expire though, so the catch block below only logs out/redirects
        // when store.state.isUserLoggedIn — here we just classify and throw.
        const sessionError = new Error('Unauthorized')
        sessionError.isSessionError = true
        throw sessionError
      }

      if (!response.ok) {
        throw new Error(`Network error: ${response.status} - ${response.statusText}`)
      }

      const jsonResponse = await response.json()

      // Error handling for GraphQL errors
      if (jsonResponse.errors) {
        const message = jsonResponse.errors.map((error) => error.message).join('\n')
        throw new Error(`GraphQL error: ${message}`)
      }

      return jsonResponse.data
    } catch (error) {
      errorReporter.reportError({
        message: error.message,
        stack: error.stack || '',
        type: 'graphql-error',
        extra: { operationName, variables }
      })

      if (failSilently) {
        return
      }
      const message = error.message || ''
      // A real session/token failure (401, or a bad/revoked refresh token) logs
      // the user out. An AppSync field-level authorization denial ("Not Authorized
      // to access … on type …", "$util.unauthorized()", "Not authorized: you must
      // be an owner …") is a PERMISSION error — the session is fine, so surface a
      // permission message and keep the user logged in. Conflating the two is what
      // made every role-gated denial look like a session expiry.
      const isSessionError =
        error.isSessionError ||
        message.includes('Invalid Refresh Token') ||
        message.includes('Refresh Token has been revoked')
      const isPermissionDenied =
        !isSessionError &&
        (message.includes('Not Authorized') ||
          message.includes('Not authorized') ||
          message.includes('Unauthorized'))

      // Tag the rethrown error so callers (e.g. App.vue's init queries) can tell a
      // genuine session failure apart from a data/permission error and decide whether
      // to end the session — without re-deriving the heuristic themselves.
      error.isSessionError = isSessionError

      // Telemetry: record the GraphQL failure (which op, status classification)
      // so client-side API failures — including the 401s that log users out — are
      // visible server-side. A logout is tracked distinctly so we can count them.
      track(isSessionError ? 'session_logout' : 'graphql_error', {
        operation: operationName,
        permissionDenied: isPermissionDenied,
        message: message.slice(0, 400)
      })

      // Anonymous (public link / embed) viewers have no session to expire, so don't
      // bounce them to login/log them out on a session error — let the caller handle it.
      if (isSessionError && store.state.isUserLoggedIn) {
        if (store?.commit) {
          store.commit('snackbar/showMessage', {
            content: 'Your session has expired. Please log in again to continue.',
            timeout: 6000,
            color: 'red',
            location: 'bottom center'
          }, { root: true })
        }
        // Flush the session_logout beacon NOW — the redirect below is an SPA
        // navigation that won't fire pagehide, so the queued event would otherwise be
        // lost (which is exactly why forced logouts were invisible in telemetry).
        flushNow()
        authHelpers.logoutAndClearStore()
        EventBus.$emit('redirect-to-login')
      } else if (isPermissionDenied) {
        if (store?.commit) {
          store.commit('snackbar/showMessage', {
            content: 'You do not have permission to do that.',
            timeout: 6000,
            color: 'red',
            location: 'bottom center'
          }, { root: true })
        }
      }
      throw error
    }
  }
}

const client = new GraphQLClient()
export default client
